import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';

interface GenerateTokenParams {
  jobId?: string;
  companyId?: string;
  expiresInDays?: number;
  maxUses?: number;
}

interface TokenResponse {
  token: string;
  referral_url: string;
  expires_at: string;
  token_id: string;
  is_existing?: boolean;
  metadata?: {
    max_uses: number | null;
    current_uses: number;
    click_count: number;
    application_count: number;
    last_clicked_at: string | null;
    is_active: boolean;
  };
  job_id?: string;
  company_id?: string;
  created_at?: string;
}

export function useSecureReferralToken() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const toast = useTranslatedToast();

  const generateToken = async (params: GenerateTokenParams): Promise<TokenResponse | null> => {
    setIsGenerating(true);
    try {
      // Verify user is authenticated before calling edge function
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        toast.raw.error('Please log in to generate referral links');
        return null;
      }

      // Supabase client automatically includes auth header from session
      const { data, error } = await supabase.functions.invoke('generate-referral-token', {
        body: {
          action: 'generate',
          job_id: params.jobId,
          company_id: params.companyId,
          expires_in_days: params.expiresInDays || 30,
          max_uses: params.maxUses,
        },
      });

      if (error) {
        console.error('Error generating token:', error);
        
        // Handle specific error cases
        if (error.status === 401) {
          toast.raw.error('Your session has expired. Please log in again.');
        } else if (error.status === 403) {
          toast.raw.error('Recruiter access is required to generate referral links');
        } else if (error.status === 429) {
          toast.raw.error(error.message || 'Too many requests. Please try again later.');
        } else {
          toast.raw.error('Failed to generate referral token');
        }
        return null;
      }

      if (!data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      return {
        token: data.token,
        referral_url: data.referral_url,
        expires_at: data.expires_at,
        token_id: data.token_id,
        is_existing: data.is_existing,
        metadata: data.metadata,
        job_id: data.job_id,
        company_id: data.company_id,
        created_at: data.created_at,
      };
    } catch (error) {
      console.error('Exception generating token:', error);
      toast.raw.error(error instanceof Error ? error.message : 'Failed to generate token');
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const validateToken = useCallback(async (token: string) => {
    setIsValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-referral-token', {
        body: {
          action: 'validate',
          token,
        },
      });

      if (error) {
        console.error('❌ [validateToken] Error:', error);
        return { valid: false, reason: 'validation_error', error };
      }

      return data;
    } catch (error) {
      console.error('❌ [validateToken] Exception:', error);
      return { valid: false, reason: 'exception', error };
    } finally {
      setIsValidating(false);
    }
  }, []);

  const deactivateToken = async (tokenId: string) => {
    try {
      const { error } = await supabase
        .from('recruiter_referral_tokens' as any)
        .update({ is_active: false } as any)
        .eq('id', tokenId as any);

      if (error) {
        throw error;
      }

      toast.raw.success('Referral token deactivated');
      return true;
    } catch (error) {
      console.error('Error deactivating token:', error);
      toast.raw.error('Failed to deactivate token');
      return false;
    }
  };

  return {
    generateToken,
    validateToken,
    deactivateToken,
    isGenerating,
    isValidating,
  };
}
